import { UserAPI } from '@business/api/user';
import { TenantUserResponse, UserId, UserResponse, UserRole, UserSettingsResponse, UserSocialId } from '@business/entities/account';
import { handleMessage, requestMessage } from '@business/messages';
import { CommonService } from '@business/services';
import { BaseBloc } from '@core/utils/bloc';
import { AppConfig } from '@core/utils/config';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { memoize, memoizeClearKey } from '@core/utils/rxjs/operators';
import { Session } from '@modules/auth/session';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { of, BehaviorSubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

export class UserBloc extends BaseBloc {
  private readonly usersRepo = new Repository<UserResponse | TenantUserResponse>({
    getItemId: user => user.id,
  });

  readonly usersLoaded$ = new BehaviorSubject<boolean>(false);

  readonly usersWithUnassigned$ = this.usersRepo.items$.pipe(map(users => R.sortBy(users, u => u.firstName)));
  readonly users$ = this.usersWithUnassigned$.pipe(
    map(users =>
      R.filter(users, u => {
        if (u.id == '9999') return false;

        return true;
      }),
    ),
  );

  readonly users = this.usersRepo.items;
  readonly selectUser = this.usersRepo.selectItem;

  readonly allUsers$ = this.usersRepo.items$;
  readonly onlyAdmins$ = this.usersRepo.items$.pipe(map(users => users.filter(u => u.role === UserRole.admin)));
  readonly staff$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role === UserRole.admin || u.role === UserRole.user)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly staffAndAffiliates$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role === UserRole.admin || u.role === UserRole.user || u.role == UserRole.affiliate)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly affiliates$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role == UserRole.affiliate)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly staffAndNoLoginUsers$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role === UserRole.admin || u.role === UserRole.user || u.role == UserRole.noLogin)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly noLoginUsers$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role === UserRole.noLogin)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly staffAndClients$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role === UserRole.admin || u.role === UserRole.user || u.role == UserRole.client)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly clients$ = this.usersRepo.items$.pipe(map(users => users.filter(u => u.role === UserRole.client)));

  readonly allUsersExceptClients$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role === UserRole.admin || u.role === UserRole.user || u.role == UserRole.affiliate)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  onReset() {
    this.usersRepo.reset();
  }

  fetchUsers = (refresh = false) => {
    return UserAPI.fetchUsers(refresh).pipe(
      this.usersRepo.ops.reset(),
      tap(() => this.usersLoaded$.next(true)),
      handleMessage({ error: requestMessage('fetch_users_error') }),
    );
  };

  fetchTenantUsers = () => {
    return UserAPI.fetchTenantUsers().pipe(
      this.usersRepo.ops.reset(),
      tap(() => this.usersLoaded$.next(true)),
      handleMessage({ error: requestMessage('fetch_users_error') }),
    );
  };

  fetchUser = (id: UserId) => {
    return UserAPI.fetchUser(id).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_users_error') }),
    );
  };

  fetchTrashedUser = (id: UserId) => {
    return UserAPI.fetchUserTrashed(id).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_users_error') }),
    );
  };

  createUser = (user: Partial<UserResponse>) => {
    return UserAPI.createUser(user).pipe(
      this.usersRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_user'),
      }),
      memoizeClearKey({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };

  updateUser = (id: UserId, user: Partial<UserResponse>) => {
    return UserAPI.updateUser(id, user).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_user'),
      }),
      memoizeClearKey({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };

  changePassword = (id: UserId, password: string) => {
    return UserAPI.changePassword(id, password).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_user'),
      }),
    );
  };

  updateUserStatus = (id: UserId, isActive: boolean) => {
    return of(null).pipe(
      switchMap(() => {
        if (isActive) {
          return UserAPI.updateUserStatus(id, true);
        }
        return showConfirmation(
          'Deactivate User',
          'This user will lose access to GrandCentral immediately and any active session will be ended. Their profile and history are kept, and you can reactivate them at any time.',
          { btnYesLabel: 'Deactivate' },
        ).pipe(switchMap(() => UserAPI.updateUserStatus(id, false)));
      }),
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage(isActive ? 'activate_user' : 'deactivate_user'),
      }),
      memoizeClearKey({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };

  deleteUser = (userId: UserId, otherUser?: UserId) => {
    return of(null).pipe(
      switchMap(() => {
        if (otherUser) {
          return UserAPI.deleteUser(userId, otherUser);
        } else {
          return showDeleteConfirmation('Delete User', 'Do you really want to remove this user?').pipe(switchMap(() => UserAPI.deleteUser(userId)));
        }
      }),
      this.usersRepo.ops.removeOne(() => userId),
      handleMessage({
        type: requestMessage('delete_user'),
      }),
      memoizeClearKey({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };

  fetchUserSocials = (id: UserId) => {
    return UserAPI.fetchUser(id).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_users_error') }),
    );
  };

  deleteSocialAccount = (socialId: UserSocialId) => {
    return showDeleteConfirmation('Delete Account', 'Do you really want to remove this account?').pipe(
      switchMap(() => UserAPI.deleteUserSocial(socialId)),
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_social_account'),
      }),
    );
  };

  updateSocialSignature = (socialId: UserSocialId, signature: string) => {
    return UserAPI.updateSocialSignature(socialId, signature).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_social_account'),
      }),
    );
  };

  updateSettings = (data: Partial<UserSettingsResponse>) => {
    return UserAPI.updateSettings(data).pipe(tap(user => Session.patchUser(user)));
  };

  editorUser = (query: string) => {
    if (!query.startsWith('@')) return [];
    return this.usersRepo.items
      .filter(item => CommonService.getFullName(item).toLowerCase().includes(query.toLocaleLowerCase().substring(1)))
      .map(user => ({ title: `${CommonService.getFullName(user)}`, value: user.id, id: user.id }));
  };

  savePermissionSettings = (key: string, value: any, userId: UserId) => {
    return UserAPI.savePermissionSettings(key, value, userId);
  };
}
